import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { emailTemplateService } from '@/services/emailTemplateService';
import type { EmailTemplate, TemplatePermission, TemplateVisibility } from '@/types/WeeklyReport';
import { Copy, Loader2, Share2, Trash2, Users, X } from 'lucide-react';
import React, { useState } from 'react';
import { TemplateVisibilityBadge } from './TemplateVisibilityBadge';

interface ShareTemplateModalProps {
    template: EmailTemplate | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
}

export const ShareTemplateModal: React.FC<ShareTemplateModalProps> = ({
    template,
    isOpen,
    onClose,
    onUpdate,
}) => {
    const { toast } = useToast();
    const [emailInput, setEmailInput] = useState('');
    const [selectedPermission, setSelectedPermission] = useState<TemplatePermission>('view');
    const [isSharing, setIsSharing] = useState(false);
    const [isRevoking, setIsRevoking] = useState<string | null>(null);
    const [isChangingVisibility, setIsChangingVisibility] = useState(false);

    if (!template) return null;

    const handleShare = async () => {
        if (!emailInput.trim()) return;

        const emails = emailInput.split(',').map(e => e.trim()).filter(e => e.includes('@'));
        
        if (emails.length === 0) {
            toast({
                title: 'Invalid email',
                description: 'Please enter valid email addresses separated by commas',
                variant: 'destructive',
            });
            return;
        }

        setIsSharing(true);
        try {
            const success = await emailTemplateService.shareTemplate(
                template.id,
                emails,
                selectedPermission
            );

            if (success) {
                toast({
                    title: 'Template shared',
                    description: `Template shared with ${emails.length} user(s)`,
                });
                setEmailInput('');
                onUpdate();
            } else {
                throw new Error('Failed to share template');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to share template',
                variant: 'destructive',
            });
        } finally {
            setIsSharing(false);
        }
    };

    const handleRevokeShare = async (email: string) => {
        setIsRevoking(email);
        try {
            const success = await emailTemplateService.revokeTemplateShare(template.id, email);

            if (success) {
                toast({
                    title: 'Access revoked',
                    description: `${email} no longer has access to this template`,
                });
                onUpdate();
            } else {
                throw new Error('Failed to revoke access');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to revoke access',
                variant: 'destructive',
            });
        } finally {
            setIsRevoking(null);
        }
    };

    const handleVisibilityChange = async (visibility: TemplateVisibility) => {
        setIsChangingVisibility(true);
        try {
            const success = await emailTemplateService.setTemplateVisibility(
                template.id,
                visibility
            );

            if (success) {
                toast({
                    title: 'Visibility updated',
                    description: `Template is now ${visibility}`,
                });
                onUpdate();
            } else {
                throw new Error('Failed to update visibility');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update visibility',
                variant: 'destructive',
            });
        } finally {
            setIsChangingVisibility(false);
        }
    };

    const handleCopyLink = () => {
        const link = `${window.location.origin}/templates?id=${template.id}`;
        navigator.clipboard.writeText(link);
        toast({
            title: 'Link copied',
            description: 'Template link copied to clipboard',
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="h-5 w-5" />
                        Share Template
                    </DialogTitle>
                    <DialogDescription>
                        Share &quot;{template.name}&quot; with others
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Visibility Section */}
                    <div className="space-y-2">
                        <Label>Visibility</Label>
                        <div className="flex items-center gap-3">
                            <Select
                                value={template.visibility || 'private'}
                                onValueChange={(value) => handleVisibilityChange(value as TemplateVisibility)}
                                disabled={isChangingVisibility}
                            >
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="private">Private</SelectItem>
                                    <SelectItem value="shared">Shared</SelectItem>
                                    <SelectItem value="organization">Organization</SelectItem>
                                </SelectContent>
                            </Select>
                            <TemplateVisibilityBadge visibility={template.visibility || 'private'} />
                            {isChangingVisibility && <Loader2 className="h-4 w-4 animate-spin" />}
                        </div>
                    </div>

                    {/* Share with Users Section */}
                    <div className="space-y-3">
                        <Label>Share with Users</Label>
                        <div className="flex gap-2">
                            <Input
                                placeholder="Enter email addresses (comma separated)"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleShare();
                                    }
                                }}
                            />
                            <Select
                                value={selectedPermission}
                                onValueChange={(value) => setSelectedPermission(value as TemplatePermission)}
                            >
                                <SelectTrigger className="w-[100px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="view">View</SelectItem>
                                    <SelectItem value="use">Use</SelectItem>
                                    <SelectItem value="edit">Edit</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button onClick={handleShare} disabled={isSharing || !emailInput.trim()}>
                                {isSharing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    'Share'
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Currently Shared With */}
                    {template.sharedWith && template.sharedWith.length > 0 && (
                        <div className="space-y-3">
                            <Label className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Shared with ({template.sharedWith.length})
                            </Label>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {template.sharedWith.map((email) => (
                                    <div
                                        key={email}
                                        className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
                                    >
                                        <span className="text-sm truncate">{email}</span>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-xs">
                                                {template.permissions || 'view'}
                                            </Badge>
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                onClick={() => handleRevokeShare(email)}
                                                disabled={isRevoking === email}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                {isRevoking === email ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <X className="h-3.5 w-3.5" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleCopyLink}>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Link
                    </Button>
                    <Button onClick={onClose}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShareTemplateModal;

